import './date-picker-field.scss';

import { DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import CalendarIcon from '@/media/icons/calendar.svg?react';

import MaskedInput, { type MaskedInputInterface } from '../masked-input';
import OutsideClickHandler from '../outside-click-handler';
import type {
  TDisableFutureDate,
  TDisablePastDate
} from './date-picker-field.types';
import useDisableDates from './hooks/useDisableDates';

dayjs.extend(customParseFormat);

type ChangedMaskedInputInterface = Omit<
  MaskedInputInterface,
  'value' | 'onChange' | 'mask'
>;

interface IProps extends ChangedMaskedInputInterface {
  value?: string;
  onChange?: (value: string) => void;
  viewFormat?: string;
  returnFormat?: string;
  mask?: string;
  /** Запрет будущих дат: true — все после сегодня, либо объект с границей disableFrom (YYYY-MM-DD) */
  disableFuture?: boolean | TDisableFutureDate;
  /** Запрет прошедших дат: true — все до сегодня, либо объект с границей disableUntil (YYYY-MM-DD) */
  disablePast?: boolean | TDisablePastDate;
  disableDays?: number;
}

const DatePickerField = forwardRef<HTMLInputElement, IProps>(
  (
    {
      value,
      onChange,
      viewFormat = 'DD.MM.YYYY',
      returnFormat = 'YYYY-MM-DD',
      mask = '__.__.____',
      disableFuture,
      disablePast,
      disableDays,
      isFrozen,
      ...rest
    },
    forwardRef
  ) => {
    const calendar = useRef<HTMLDivElement>(null);

    const [focused, setFocused] = useState(false);

    const { disableDate } = useDisableDates({
      disableFuture,
      disablePast,
      disableDays
    });

    const date = useMemo(() => {
      if (!value) return null;
      // Если value в формате returnFormat (YYYY-MM-DD)
      if (value.length === returnFormat.length) {
        const parsed = dayjs(value, returnFormat);
        return parsed.isValid() ? parsed : null;
      }
      // Если value в формате viewFormat (DD.MM.YYYY)
      if (value.length === viewFormat.length) {
        const parsed = dayjs(value, viewFormat);
        return parsed.isValid() ? parsed : null;
      }
      return null;
    }, [value, returnFormat, viewFormat]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value.replace(/_/g, '');
      if (newValue.length === viewFormat.length) {
        const parsedDate = dayjs(newValue, viewFormat);
        if (parsedDate.isValid()) {
          onChange?.(parsedDate.format(returnFormat));
        } else {
          onChange?.(newValue);
        }
      } else {
        onChange?.(newValue);
      }
    };

    const handleCalendarChange = (date: Dayjs | null) => {
      if (date) {
        onChange?.(date.format(returnFormat));
        setFocused(false);
      } else {
        onChange?.('');
      }
    };

    useEffect(() => {
      if (value?.length !== returnFormat.length || isFrozen) return;
      const parsedDate = dayjs(value, returnFormat);
      if (!parsedDate.isValid()) return;

      const todayStart = dayjs().startOf('day');
      const inputStart = parsedDate.startOf('day');
      const dateDifference = inputStart.diff(todayStart, 'days');

      if (disableFuture) {
        if (typeof disableFuture === 'boolean' && dateDifference > 0) {
          onChange?.(dayjs().format(returnFormat));
          return;
        }
        if (
          typeof disableFuture === 'object' &&
          disableFuture.disableBy &&
          disableFuture.disableFrom
        ) {
          const disableFromDate = dayjs(disableFuture.disableFrom);
          if (parsedDate.isAfter(disableFromDate, 'day')) {
            onChange?.(dayjs().format(returnFormat));
            return;
          }
        }
      }

      if (disablePast) {
        if (typeof disablePast === 'boolean' && dateDifference < 0) {
          onChange?.(dayjs().format(returnFormat));
          return;
        }
        if (
          typeof disablePast === 'object' &&
          disablePast.disableBy &&
          disablePast.disableUntil
        ) {
          const disableUntilDate = dayjs(disablePast.disableUntil);
          if (parsedDate.isBefore(disableUntilDate, 'day')) {
            onChange?.(dayjs().format(returnFormat));
            return;
          }
        }
      }
    }, [value, returnFormat, disableFuture, disablePast, isFrozen, onChange]);

    useEffect(() => {
      if (value && value?.length > viewFormat.length) {
        const timestamp = Date.parse(value);
        if (Number.isNaN(timestamp)) return;
        const formattedDate = dayjs(value).format(returnFormat);
        onChange?.(formattedDate);
      }
    }, [value, viewFormat, returnFormat, onChange]);

    const formattedValue = useMemo(() => {
      if (!value) return '';
      // Если value в формате returnFormat (YYYY-MM-DD), конвертируем в viewFormat
      if (value.length === returnFormat.length) {
        const parsed = dayjs(value, returnFormat);
        return parsed.isValid() ? parsed.format(viewFormat) : '';
      }
      // Если value в процессе ввода (viewFormat) или короче маски
      if (value.length <= mask.length) {
        return value;
      }
      return '';
    }, [value, returnFormat, viewFormat, mask]);

    if (viewFormat.length !== mask.length)
      return (
        <span>
          <b>format</b> and <b>mask</b> properties must be the same length
        </span>
      );

    return (
      <OutsideClickHandler
        onOutsideClick={(element) => {
          if (!calendar.current?.contains(element)) setFocused(false);
        }}
      >
        <div className="date-picker-field">
          <MaskedInput
            {...rest}
            inputMode="numeric"
            ref={forwardRef}
            value={formattedValue}
            onChange={handleInputChange}
            endContent={
              <span className="date-picker-field__icon">
                <CalendarIcon />
              </span>
            }
            onClick={(e) => {
              if (!isFrozen) setFocused((prev) => !prev);
              rest.onClick?.(e);
            }}
            mask={mask}
            replacement={rest.replacement || { _: /\d/ }}
            autoComplete="off"
            isFrozen={isFrozen}
          />

          <div ref={calendar}>
            <DatePicker
              picker="date"
              value={date}
              onChange={handleCalendarChange}
              getPopupContainer={() => calendar.current || document.body}
              className="date-picker-field__calendar"
              classNames={{
                popup: {
                  root: 'date-picker-field__calendar-dropdown'
                }
              }}
              format={viewFormat}
              open={focused}
              disabledDate={disableDate}
            />
          </div>
        </div>
      </OutsideClickHandler>
    );
  }
);

DatePickerField.displayName = 'DatePickerField';

export default DatePickerField;
